import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'fileInput',
    'validateButton',
    'summarySection',
    'counts',
    'globalErrors',
    'errorsSection',
    'errorGroups',
    'downloadReportLink',
    'clearUploadButton',
    'createButton',
    'createModalMessage',
    'createConfirmButton',
  ]

  static values = {
    validateUrl: String,
    createUrl: String,
  }

  connect() {
    this.uploadToken = null
    this.readyToCreateCount = 0
    this.needAttentionCount = 0
    this.isCreating = false
    this.updateValidateButtonState()
  }

  updateValidateButtonState() {
    this.validateButtonTarget.disabled =
      this.isCreating || !this.fileInputTarget.files?.length
  }

  validateUpload() {
    const file = this.fileInputTarget.files[0]
    if (!file) {
      window.alert('Please select a CSV file.')
      return
    }

    this.validateButtonTarget.disabled = true

    const formData = new FormData()
    formData.append('file', file)

    return fetch(this.validateUrlValue, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': document.querySelector("meta[name='csrf-token']")
          ?.content,
      },
      body: formData,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Validation request failed')
        }

        return response.json()
      })
      .then((data) => {
        this.uploadToken = data.token
        this.readyToCreateCount = data.counts.ready_to_create
        this.needAttentionCount = data.counts.need_attention

        this.summarySectionTarget.classList.remove('hidden')
        this.countsTarget.textContent = `${data.counts.rows_found} rows found · ${data.counts.ready_to_create} ready to create · ${data.counts.need_attention} need attention.`

        this.renderGlobalErrors(data.global_errors || [])
        this.renderErrorGroups(data.errors_by_reason || {})

        this.downloadReportLinkTarget.href = data.report_url
        this.updateErrorActionState()
        this.updateCreateButtonState()

        return data
      })
      .catch(() => {
        window.alert('There was an error validating the upload.')

        return null
      })
      .finally(() => {
        this.updateValidateButtonState()
      })
  }

  clearUpload() {
    if (this.isCreating) {
      return
    }

    this.fileInputTarget.value = ''
    this.updateValidateButtonState()
    this.summarySectionTarget.classList.add('hidden')
    this.uploadToken = null
    this.readyToCreateCount = 0
    this.needAttentionCount = 0
    this.updateErrorActionState()
    this.closeCreateModal()
  }

  prepareCreateModal() {
    if (this.isCreating) {
      return
    }

    if (!this.uploadToken) {
      window.alert('Please validate a file before creating program offers.')
      return
    }

    const invitationLabel = this.pluralizeCount(
      this.readyToCreateCount,
      'invitation',
    )
    const rowLabel = this.pluralizeCount(this.needAttentionCount, 'row')
    const createButtonLabel = this.pluralizeCount(
      this.readyToCreateCount,
      'Create Invitation',
    )

    const skipMessage =
      this.needAttentionCount > 0
        ? ` ${this.needAttentionCount} ${rowLabel} will be skipped - see report.`
        : ''
    this.createModalMessageTarget.textContent = `Create ${this.readyToCreateCount} ${invitationLabel}?${skipMessage}`
    this.createConfirmButtonTarget.textContent = createButtonLabel

    const modal = this.createButtonTarget
      .closest('[data-controller="dialog"]')
      ?.querySelector('dialog')

    if (modal && !modal.open) {
      modal.showModal()
    }
  }

  closeCreateModal() {
    const modal = this.createButtonTarget
      .closest('[data-controller="dialog"]')
      ?.querySelector('dialog')

    if (modal?.open) {
      modal.close()
    }
  }

  confirmCreateValidRows() {
    if (this.isCreating) {
      return
    }

    if (!this.uploadToken) {
      window.alert('Please validate a file before creating program offers.')
      return
    }

    this.closeCreateModal()
    this.setCreatingState(true)

    return fetch(this.createUrlValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': document.querySelector("meta[name='csrf-token']")
          ?.content,
      },
      body: JSON.stringify({ token: this.uploadToken }),
    })
      .then((response) =>
        response.json().then((body) => ({ status: response.status, body })),
      )
      .then(({ status, body }) => {
        if (status >= 400) {
          window.alert(
            (body.errors || ['Unable to create program offers.']).join('\n'),
          )
          this.setCreatingState(false)
          return null
        }

        window.location.href = body.redirect_url

        return body
      })
      .catch(() => {
        window.alert('There was an error creating program offers.')
        this.setCreatingState(false)

        return null
      })
  }

  setCreatingState(isCreating) {
    this.isCreating = isCreating

    this.fileInputTarget.disabled = isCreating
    this.createConfirmButtonTarget.disabled = isCreating
    this.createConfirmButtonTarget.classList.toggle('opacity-50', isCreating)
    this.createConfirmButtonTarget.classList.toggle(
      'cursor-not-allowed',
      isCreating,
    )
    this.createConfirmButtonTarget.classList.toggle(
      'pointer-events-none',
      isCreating,
    )

    this.updateErrorActionState()

    this.updateValidateButtonState()
    this.updateCreateButtonState()
  }

  updateErrorActionState() {
    const shouldDisable = this.isCreating || this.needAttentionCount === 0

    this.clearUploadButtonTargets.forEach((button) => {
      button.disabled = shouldDisable
      button.classList.toggle('opacity-50', shouldDisable)
      button.classList.toggle('cursor-not-allowed', shouldDisable)
      button.classList.toggle('pointer-events-none', shouldDisable)
    })

    this.downloadReportLinkTarget.classList.toggle(
      'pointer-events-none',
      shouldDisable,
    )
    this.downloadReportLinkTarget.classList.toggle('opacity-50', shouldDisable)
    this.downloadReportLinkTarget.setAttribute(
      'aria-disabled',
      shouldDisable ? 'true' : 'false',
    )
    this.downloadReportLinkTarget.tabIndex = shouldDisable ? -1 : 0
  }

  updateCreateButtonState() {
    const shouldDisable = this.isCreating || this.readyToCreateCount === 0
    this.createButtonTarget.disabled = shouldDisable
    this.createButtonTarget.classList.toggle('opacity-50', shouldDisable)
    this.createButtonTarget.classList.toggle(
      'cursor-not-allowed',
      shouldDisable,
    )
  }

  pluralizeCount(count, singular) {
    return count === 1 ? singular : `${singular}s`
  }

  renderGlobalErrors(errors) {
    if (!errors.length) {
      this.globalErrorsTarget.classList.add('hidden')
      this.globalErrorsTarget.textContent = ''
      return
    }

    this.globalErrorsTarget.classList.remove('hidden')
    this.globalErrorsTarget.textContent = errors.join(' ')
  }

  renderErrorGroups(groups) {
    this.errorGroupsTarget.innerHTML = ''

    const entries = Object.entries(groups)
    if (!entries.length) {
      this.errorsSectionTarget.classList.add('hidden')
      return
    }

    this.errorsSectionTarget.classList.remove('hidden')

    entries.forEach(([reason, rows]) => {
      const rowElement = document.createElement('p')
      const rowList = rows.length
        ? `${this.pluralizeCount(rows.length, 'Row')}: ${rows.join(', ')}`
        : 'No rows listed.'
      rowElement.className = 'text-intoo-text'
      rowElement.textContent = `${reason}: ${rowList}`
      this.errorGroupsTarget.appendChild(rowElement)
    })
  }
}
